import BusinessDateFormats from '@/modules/common/filters/BusinessDateFormats';

export const ISO_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Formats date to ISO format YYYY-MM-DD
 * @param {Date} date - Date to format
 * @returns {string} - Formatted date in YYYY-MM-DD format
 */
const formatToISODate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Gets today's date in YYYY-MM-DD format, respecting the user's timezone
 * @returns {string} - Today's date in YYYY-MM-DD format
 */
export const formatTodayToISODate = () => {
  const today = new Date();
  return formatToISODate(today);
};

/**
 * Calculates the end date of a package based on validity parameters
 * @param {string} validFrom - Starting date in YYYY-MM-DD format
 * @param {number} expiration - Duration number
 * @param {string} expirationUnit - Duration unit (d: days, w: weeks, m: months, y: years)
 * @returns {string} - End date in YYYY-MM-DD format
 */
export const calculateValidUntil = (validFrom, expiration, expirationUnit) => {
  const unitKey = expirationUnit;

  const unitsMapping = {
    d: 'days',
    w: 'weeks',
    m: 'months',
    y: 'years',
  };
  const unit = unitsMapping[unitKey];
  if (!unit) return null;

  const date = new Date(validFrom);

  switch (unit) {
    case 'days':
      date.setDate(date.getDate() + expiration);
      break;
    case 'weeks':
      date.setDate(date.getDate() + (expiration * 7));
      break;
    case 'months':
      date.setMonth(date.getMonth() + expiration);
      date.setDate(date.getDate() - 1);
      break;
    case 'years':
      date.setFullYear(date.getFullYear() + expiration);
      date.setDate(date.getDate() - 1);
      break;
    default:
      return null;
  }

  return formatToISODate(date);
};

/**
 * Returns the later of the given date or today
 * @param {string} date - Date to check in YYYY-MM-DD format
 * @returns {string} - Either today or the given date, whichever is later, in YYYY-MM-DD format
 */
export const todayIfBefore = (date) => {
  if (!date) return formatToISODate(new Date());

  const inputDate = new Date(date);
  const today = new Date();

  [today, inputDate].forEach((d) => d.setHours(0, 0, 0, 0));

  const largestTimestamp = Math.max(inputDate.getTime(), today.getTime());
  return formatToISODate(new Date(largestTimestamp));
};

/**
 * Formats a date in "day month year" format, always showing the year
 * @param {string|Date} date - Date to format
 * @param {boolean} isFullMonth - Whether to use full month name
 * @returns {string} - Formatted date in "day month year" format
 */
export const formatDateWithYear = (date, isFullMonth = true) => {
  if (!date) return '';
  const dateObj = new Date(date);
  if (Number.isNaN(dateObj.getTime())) return '';
  return BusinessDateFormats.dateWithYear(dateObj.toISOString(), isFullMonth);
};
